/**
 * Cores de gráficos (ApexCharts) a partir dos tokens --brand-* / store de branding.
 * Preferir CSS vars já aplicadas por useApplyBrandingCssVars; fallbacks estáveis.
 * Chamar dentro de `computed` para reagir quando o branding carregar.
 */

import { useBrandingStore } from '@/stores/branding';

const FALLBACK_PRIMARY = '#4361ee';
const FALLBACK_ACCENT = '#0d6efd';

export function readBrandCssVar(name, fallback = '') {
    if (typeof document === 'undefined') {
        return fallback;
    }
    const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return value || fallback;
}

function readStoreColors() {
    try {
        const branding = useBrandingStore();
        return {
            primary: branding.primaryColor || null,
            accent: branding.accentColor || null,
        };
    } catch {
        return { primary: null, accent: null };
    }
}

export function getBrandPrimaryHex(fallback = FALLBACK_PRIMARY) {
    const fromCss = readBrandCssVar('--brand-primary', '');
    if (fromCss) {
        return fromCss;
    }
    return readStoreColors().primary || fallback;
}

export function getBrandAccentHex(fallback = FALLBACK_ACCENT) {
    const fromCss = readBrandCssVar('--brand-accent', '');
    if (fromCss) {
        return fromCss;
    }
    return readStoreColors().accent || getBrandPrimaryHex(fallback);
}

/**
 * Paleta para séries múltiplas: primary, accent e tons de apoio.
 * @param {number} count
 * @returns {string[]}
 */
export function getBrandChartPalette(count = 4) {
    const primary = getBrandPrimaryHex();
    const accent = getBrandAccentHex();
    const palette = [primary, accent, '#4caf50', '#ff9800', '#9c27b0', '#00bcd4'];
    if (count <= 0) {
        return [primary];
    }
    if (count <= palette.length) {
        return palette.slice(0, count);
    }
    const out = [...palette];
    while (out.length < count) {
        out.push(palette[out.length % palette.length]);
    }
    return out;
}

/**
 * Donut "Pendentes / Você / IA": status âmbar + marca + destaque.
 * @returns {[string, string, string]}
 */
export function getBrandCorrecoesDonutColors() {
    return ['#e2a03f', getBrandPrimaryHex(), getBrandAccentHex()];
}

/**
 * Donut inscritos: Ativos (marca) / Inativos / Concluídos.
 * @returns {[string, string, string]}
 */
export function getBrandInscritosDonutColors() {
    return [getBrandPrimaryHex(), '#ffa726', '#00c853'];
}
